
import {
    LocationModel,
    PanelDashboardModel,
    PanelDashboardSortTypeEnum,
    PlanItemModel,
    ProcessTypeEnum,
    TenantUserModel,
    WorkplaceModel,
} from '@/libs/Api';
import { CrudAction, CrudReponse } from '@/libs/core/+state/models/crud-action';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import Page from '@/Page.vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import WorkTaskCreateDialog from '@/libs/work-tasks/components/WorkTaskCreateDialog.vue';
import { authStore } from '@/libs/auth/+store/store';
import { AuthAction, AuthGetter } from '@/libs/auth/models/auth-state';
import { panelDashboardsStore } from '@/libs/panel-dashboards/+state/store';
import { planItemsStore } from '@/libs/plan-items/+state/store';
import { locationsStore } from '@/libs/locations/+state/store';
import { workplacesStore } from '@/libs/workplaces/+state/store';
import { NotificationScope } from '@/libs/common/model/NotificationScope';
import { createCrudQueryPayload } from '@/libs/core/+state/models/crud-query-payload';
import DeactivateQuickLoginDialog from '@/libs/common/components-smart/DeactivateQuickLoginDialog.vue';
import { AuthenticationType } from '@/libs/auth/models/auth-type';
import { PublicClientApplication } from '@azure/msal-browser';
import { GlobalVariables } from '@/libs/core/global';
import moment from 'moment/moment';
import { ToastUtility } from '@syncfusion/ej2-notifications';
import show = ToastUtility.show;

@Component({
    methods: { show },

    computed: {
        ToastUtility() {
            return ToastUtility;
        },
    },
    components: {
        DeactivateQuickLoginDialog,
        WorkTaskCreateDialog,
    },
})
export default class PanelDashboard extends Page {
    loading = true;
    isLocked = false;
    unlockDialog = false;
    tenantUser: TenantUserModel = authStore.useGetter(AuthGetter.GetTenantUser) as TenantUserModel;
    @Prop() tenantId!: string;
    @Prop() panelId!: string;
    @Prop({ default: true }) showHeader!: boolean;
    protected ProcessTypeEnum: typeof ProcessTypeEnum = ProcessTypeEnum;
    protected items: { workplace: WorkplaceModel; issue: PlanItemModel | null }[] = [];
    private refreshInterval: any = null;

    get panelDashboard(): PanelDashboardModel {
        return panelDashboardsStore.useGetter(CrudGetter.Detail);
    }

    get sortedItems(): { workplace: WorkplaceModel; issue: PlanItemModel | null }[] {
        if (!this.items) {
            return [];
        }
        const numericPriority = this.items.all(
            (x) => !x.issue?.auditFaultType?.priority || !isNaN(Number(x.issue?.auditFaultType?.priority))
        );
        const now = moment().format('YYYY-MM-DDTHH:mm:ss');
        if (this.panelDashboard != null) {
            if (this.panelDashboard.sortType == PanelDashboardSortTypeEnum.ByWorkplace) {
                if (this.panelDashboard.sortAscending) {
                    return this.items
                        .orderBy(
                            (x) => x.workplace.name,
                            (a: any, b: any) => a.localeCompare(b!)
                        )
                        .thenBy(
                            (x) =>
                                numericPriority
                                    ? Number(x.issue?.auditFaultType?.priority ?? '9999')
                                    : x.issue?.auditFaultType?.priority,
                            (a: any, b: any) => (numericPriority ? Number(a) - Number(b) : a.localeCompare(b!))
                        )
                        .thenBy(
                            (x) =>
                                x.issue?.isWorkplaceShutdown
                                    ? new Date(x.issue?.shutdownToDate ?? now).getTime() -
                                      new Date(x.issue?.shutdownFromDate ?? now).getTime()
                                    : 2147483647 * 1000,
                            (a, b) => a - b
                        )
                        .toArray();
                } else {
                    return this.items
                        .orderByDescending(
                            (x) => x.workplace.name,
                            (a: any, b: any) => a.localeCompare(b!)
                        )
                        .thenByDescending(
                            (x) =>
                                numericPriority
                                    ? Number(x.issue?.auditFaultType?.priority ?? '9999')
                                    : x.issue?.auditFaultType?.priority,
                            (a: any, b: any) => (numericPriority ? Number(a) - Number(b) : a.localeCompare(b!))
                        )
                        .thenByDescending(
                            (x) =>
                                x.issue?.isWorkplaceShutdown
                                    ? new Date(x.issue?.shutdownToDate ?? now).getTime() -
                                      new Date(x.issue?.shutdownFromDate ?? now).getTime()
                                    : 2147483647 * 1000,
                            (a, b) => a - b
                        )
                        .toArray();
                }
            } else if (this.panelDashboard.sortType == PanelDashboardSortTypeEnum.ByReportTime) {
                const itemsWithIssue = this.items.filter((x) => x.issue != null);
                const itemsWithoutIssue = this.items.filter((x) => x.issue == null);
                if (this.panelDashboard.sortAscending) {
                    return [
                        ...itemsWithIssue
                            .orderBy(
                                (x) => new Date(x.issue!.time ?? now).getTime(),
                                (a, b) => b - a
                            )
                            .thenBy(
                                (x) => x.workplace.name ?? '',
                                (a, b) => a.localeCompare(b!)
                            )
                            .toArray(),
                        ...itemsWithoutIssue
                            .orderBy(
                                (x) => x.workplace.name ?? '',
                                (a, b) => a.localeCompare(b!)
                            )
                            .toArray(),
                    ];
                } else {
                    return [
                        ...itemsWithIssue
                            .orderByDescending(
                                (x) => new Date(x.issue!.time ?? now).getTime(),
                                (a, b) => b - a
                            )
                            .orderByDescending(
                                (x) => x.workplace.name ?? '',
                                (a, b) => a.localeCompare(b!)
                            )
                            .toArray(),
                        ...itemsWithoutIssue
                            .orderBy(
                                (x) => x.workplace.name ?? '',
                                (a, b) => a.localeCompare(b!)
                            )
                            .toArray(),
                    ];
                }
            } else if (this.panelDashboard.sortType == PanelDashboardSortTypeEnum.ByShutdownDuration) {
                const itemsWithIssue = this.items.filter((x) => x.issue != null);
                const itemsWithoutIssue = this.items.filter((x) => x.issue == null);
                if (this.panelDashboard.sortAscending) {
                    return [
                        ...itemsWithIssue
                            .orderBy(
                                (x) =>
                                    x.issue?.isWorkplaceShutdown
                                        ? new Date(x.issue?.shutdownToDate ?? now).getTime() -
                                          new Date(x.issue?.shutdownFromDate ?? now).getTime()
                                        : 2147483647 * 1000,
                                (a, b) => a - b
                            )
                            .thenBy(
                                (x) => new Date(x.issue!.time ?? now).getTime(),
                                (a, b) => a - b
                            )
                            .thenBy(
                                (x) => x.workplace.name ?? '',
                                (a, b) => a.localeCompare(b!)
                            )
                            .toArray(),
                        ...itemsWithoutIssue
                            .orderBy(
                                (x) => x.workplace.name ?? '',
                                (a, b) => a.localeCompare(b!)
                            )
                            .toArray(),
                    ];
                } else {
                    return [
                        ...itemsWithIssue
                            .orderByDescending(
                                (x) =>
                                    x.issue?.isWorkplaceShutdown
                                        ? new Date(x.issue?.shutdownToDate ?? now).getTime() -
                                          new Date(x.issue?.shutdownFromDate ?? now).getTime()
                                        : 0,
                                (a, b) => a - b
                            )
                            .thenByDescending(
                                (x) => new Date(x.issue!.time ?? now).getTime(),
                                (a, b) => a - b
                            )
                            .thenByDescending(
                                (x) => x.workplace.name ?? '',
                                (a, b) => a.localeCompare(b!)
                            )
                            .toArray(),
                        ...itemsWithoutIssue.orderBy(
                            (x) => x.workplace.name ?? '',
                            (a, b) => a.localeCompare(b!)
                        ),
                    ];
                }
            } else if (this.panelDashboard.sortType == PanelDashboardSortTypeEnum.ByFaultPriority) {
                const itemsWithIssue = this.items.filter((x) => x.issue != null);
                const itemsWithoutIssue = this.items.filter((x) => x.issue == null);
                if (this.panelDashboard.sortAscending) {
                    return [
                        ...itemsWithIssue
                            .orderBy(
                                (x) =>
                                    numericPriority
                                        ? Number(x.issue?.auditFaultType?.priority ?? '9999')
                                        : x.issue?.auditFaultType?.priority,
                                (a: any, b: any) => (numericPriority ? Number(a) - Number(b) : a.localeCompare(b!))
                            )
                            .thenBy(
                                (x) =>
                                    x.issue?.isWorkplaceShutdown
                                        ? new Date(x.issue?.shutdownToDate ?? now).getTime() -
                                          new Date(x.issue?.shutdownFromDate ?? now).getTime()
                                        : 2147483647 * 1000,
                                (a, b) => a - b
                            )
                            .thenBy(
                                (x) => new Date(x.issue!.time ?? now).getTime(),
                                (a, b) => a - b
                            )
                            .thenBy(
                                (x) => x.workplace.name ?? '',
                                (a, b) => a.localeCompare(b!)
                            )
                            .toArray(),
                        ...itemsWithoutIssue
                            .orderBy(
                                (x) => x.workplace.name ?? '',
                                (a, b) => a.localeCompare(b!)
                            )
                            .toArray(),
                    ];
                } else {
                    return [
                        ...itemsWithIssue
                            .orderByDescending(
                                (x) =>
                                    numericPriority
                                        ? Number(x.issue?.auditFaultType?.priority ?? '9999')
                                        : x.issue?.auditFaultType?.priority,
                                (a: any, b: any) => (numericPriority ? Number(a) - Number(b) : a.localeCompare(b!))
                            )
                            .thenByDescending(
                                (x) =>
                                    x.issue?.isWorkplaceShutdown
                                        ? new Date(x.issue?.shutdownToDate ?? now).getTime() -
                                          new Date(x.issue?.shutdownFromDate ?? now).getTime()
                                        : 0,
                                (a, b) => a - b
                            )
                            .thenByDescending(
                                (x) => new Date(x.issue!.time ?? now).getTime(),
                                (a, b) => a - b
                            )
                            .thenByDescending(
                                (x) => x.workplace.name ?? '',
                                (a, b) => a.localeCompare(b!)
                            )
                            .toArray(),
                        ...itemsWithoutIssue.orderBy(
                            (x) => x.workplace.name ?? '',
                            (a, b) => a.localeCompare(b!)
                        ),
                    ];
                }
            }
        }
        return this.items;
    }

    async mounted() {
        authStore.dispatch(AuthAction.SetTvMode, true);
        this.isLocked = authStore.useGetter(AuthGetter.GetIsLocked) ?? false;
        await this.loadPanel();
        this.refreshInterval = setInterval(() => {
            this.loadPanel(true);
        }, 10000);
        this.autoScrollPanelDashboardTable();
    }

    async destroyed() {
        authStore.dispatch(AuthAction.SetTvMode, false);
        clearInterval(this.refreshInterval);
    }

    @Watch('panelId')
    async panelIdChanged() {
        await this.loadPanel();
    }

    async loadPanel(silent = false) {
        if (!silent) {
            this.loading = true;
        }
        panelDashboardsStore.dispatch(CrudAction.Get, { id: this.panelId });
        const panelDashboard = await this.subscribe(panelDashboardsStore, CrudReponse.Get);
        if (panelDashboard.language != this.$i18n.locale) {
            this.$i18n.locale = panelDashboard.language;
            window.localStorage.language = panelDashboard.language;
            this.$vuetify.lang.current = panelDashboard.language;
            GlobalVariables.lang = panelDashboard.language;
            moment.locale(this.$vuetify.lang.current);
        }
        await this.loadItems();
        if (!silent) {
            this.loading = false;
        }
    }

    async loadItems() {
        planItemsStore.dispatch(
            CrudAction.GetAllBasic,
            createCrudQueryPayload(
                { field: 'time', index: 1, order: 'desc' },
                [
                    { field: 'processType', op: 'eq', comparand: ProcessTypeEnum.IssueSolving },
                    {
                        field: 'id',
                        op: 'wrap',
                        comparand: [
                            {
                                field: 'planItemState',
                                op: 'eq',
                                comparand: 'WaitingToPerform',
                                junction: 'or',
                            },
                            {
                                field: 'id',
                                op: 'wrap',
                                comparand: [
                                    {
                                        field: 'isWorkplaceShutdown',
                                        op: 'eq',
                                        comparand: true,
                                    },
                                    {
                                        field: 'shutdownToDate',
                                        op: 'eq',
                                        comparand: null,
                                    },
                                ],
                                junction: 'or',
                            },
                        ],
                    },
                ],
                {
                    pageIndex: 1,
                    pageSize: 999,
                },
                false,
                'PanelDashboard',
                true
            )
        );
        await this.subscribe(planItemsStore, CrudReponse.GetAllBasic, 'PanelDashboard');
        const panelDashboard = panelDashboardsStore.useGetter(CrudGetter.Detail);
        const notificationScope: NotificationScope = panelDashboard?.locationScope
            ? JSON.parse(panelDashboard?.locationScope)
            : null;
        let items = [...planItemsStore.useGetter(CrudGetter.DataBasic, 'PanelDashboard')] as PlanItemModel[];
        const locations: LocationModel[] = locationsStore.useGetter(CrudGetter.Data) ?? [];
        let workplaces: WorkplaceModel[] = workplacesStore.useGetter(CrudGetter.Data) ?? [];
        const activeWorkplaces = notificationScope?.workplaces ?? [];
        const activeLocations = notificationScope?.locations ?? [];
        const locationScopes = locations
            .filter((x) => x.locationParents!.some((y) => activeLocations.includes(y.parentalLocationId!)))
            .map((x) => x.id!)
            .concat(activeLocations!);
        if (activeWorkplaces.length > 0) {
            items = items.filter((x) => activeWorkplaces.includes(x.workplaceId!));
            workplaces = workplaces.filter((x) => activeWorkplaces.includes(x.id!));
        } else if (activeLocations.length > 0) {
            const workplacesToFilter = workplaces.filter((x) => locationScopes.includes(x.locationId!)).map((x) => x.id!);
            items = items.filter((x) => workplacesToFilter.includes(x.workplaceId!));
            workplaces = workplaces.filter((x) => workplacesToFilter.includes(x.id!));
        }
        workplaces = workplaces.sort((a, b) => a.name!.localeCompare(b.name!));
        const output: { workplace: WorkplaceModel; issue: PlanItemModel | null }[] = [];
        for (const workplace of workplaces) {
            const filteredItems = items
                .filter((x) => x.workplaceId == workplace.id)
                .sort((a, b) => new Date(a.time!).getTime() - new Date(b.time!).getTime());
            if (filteredItems.length == 0) {
                output.push({
                    workplace,
                    issue: null,
                });
            } else if (filteredItems.length == 0) {
                output.push({
                    workplace,
                    issue: filteredItems[0],
                });
            } else {
                for (const item of filteredItems) {
                    output.push({
                        workplace,
                        issue: item,
                    });
                }
            }
        }
        this.items = output;
    }

    getBackgroundColor(planItem: PlanItemModel) {
        return `#${planItem?.auditFaultType?.color ?? '27a800'}`;
    }

    getFontColor(planItem: PlanItemModel): string {
        const backgroundColor = this.getBackgroundColor(planItem).replace('#', '');
        return this.useWhiteFont(backgroundColor) ? '#fff' : '#000';
    }

    lock() {
        this.isLocked = true;
        authStore.dispatch(AuthAction.SetIsLocked, true);
    }

    unlock() {
        this.unlockDialog = true;
    }

    private signout() {
        authStore.dispatch(AuthAction.SignOut);
        const authenticationType = authStore.useGetter(AuthGetter.GetAuthenticationType) as AuthenticationType;
        if (authenticationType == AuthenticationType.AzureActiveDirectory) {
            const azureTenantId = authStore.useGetter(AuthGetter.GetAzureTenantId);
            const azureAppId = authStore.useGetter(AuthGetter.GetAzureAppId);
            const msalConfig = {
                auth: {
                    clientId: azureAppId,
                    authority: 'https://login.microsoftonline.com/' + azureTenantId,
                },
            };
            const msalInstance = new PublicClientApplication(msalConfig);
            msalInstance.handleRedirectPromise();
            msalInstance.logoutPopup();
        }
    }

    autoScrollPanelDashboardTable() {
        const table = document.querySelector('.panel-dashboard-table')!.parentNode as HTMLElement;

        if (!table) {
            console.warn("Element '.panel-dashboard-table' nebyl nalezen.");
            return;
        }

        let direction: 'down' | 'up' = 'down';
        let paused = false;
        let autoscroll = false;

        const scroll = () => {
            if (paused) return; // Pokud je pozastaveno, přeskočí scrollování

            const maxScrollTop = table.scrollHeight - table.clientHeight;

            if (direction === 'down') {
                autoscroll = true;
                table.scrollTop += 1; // Nastavte hodnotu podle požadované rychlosti
                if (table.scrollTop + 1 >= maxScrollTop) {
                    direction = 'up';
                    setTimeout(scroll, 3000); // Po dosažení dolního okraje čeká 3 sekundy
                    return;
                }
            } else {
                autoscroll = true;
                table.scrollTop -= 1; // Posun nahoru
                if (table.scrollTop - 1 <= 0) {
                    direction = 'down';
                    setTimeout(scroll, 3000); // Po dosažení horního okraje čeká 3 sekundy
                    return;
                }
            }
            requestAnimationFrame(scroll); // Nepřetržitý posun
        };
        let timeout: any = null;

        const pauseScrolling = (event: any) => {
            if (autoscroll) {
                autoscroll = false;
                return;
            }
            paused = true;
            clearTimeout(timeout);
            timeout = setTimeout(() => {
                paused = false; // Obnoví scrollování po 5 sekundách
                scroll();
            }, 3000);
        };

        // Posluchače událostí pro interakci uživatele
        document.addEventListener('scroll', pauseScrolling, true);
        document.addEventListener('mousemove', pauseScrolling, true);
        document.addEventListener('click', pauseScrolling, true);
        document.addEventListener('keydown', pauseScrolling, true);

        // Spustí automatické scrollování
        timeout = setTimeout(() => {
            scroll();
        }, 3000);
    }
}
